<template>
  <div>
    <v-card class="plate" color="surface" rounded="lg" variant="flat" elevation="4">
      <div class="plate-header">
        <div class="plate-title">{{ title }}</div>
        <div class="plate-stamp bg-msq-yellow">{{ stamp }}</div>
      </div>

      <div class="trace-run">
        <div class="trace-run-row">
          <span class="trace-status">
            <v-icon icon="mdi-check-circle" size="18" color="accent-green" /> Workflow Run
          </span>
          <span class="trace-run-meta text-disabled">
            <span class="pill">completed</span>
            {{ runMeta }}
          </span>
        </div>
      </div>

      <div class="trace-sub-head text-disabled">Sub-agents ({{ agents.length }}/{{ agents.length }})</div>

      <div class="trace-agents">
        <div v-for="agent in agents" :key="agent.name" class="ta">
          <component
            :is="isExpandable(agent) ? 'button' : 'div'"
            class="ta-row"
            :class="{ 'ta-row--static': !isExpandable(agent) }"
            :type="isExpandable(agent) ? 'button' : undefined"
            :aria-expanded="isExpandable(agent) ? String(!!expanded[agent.name]) : undefined"
            @click="isExpandable(agent) && toggle(agent.name)"
          >
            <span class="ta-name">
              <v-icon icon="mdi-check-circle" size="15" color="accent-green" /> {{ agent.name }}
            </span>
            <span class="ta-tok text-disabled">{{ agent.tokens }}</span>
            <v-icon
              v-if="isExpandable(agent)"
              icon="mdi-chevron-down"
              size="18"
              class="ta-caret text-disabled"
              :class="{ 'ta-caret--collapsed': !expanded[agent.name] }"
            />
          </component>

          <div v-if="isExpandable(agent) && expanded[agent.name]" class="ta-detail">
            <ToolCallRow v-for="(call, ci) in agent.toolCalls" :key="ci" :call="call" :initially-expanded="!!call.json" />
            <div v-if="agent.reportHeadline" class="ta-report">
              <div class="ta-report-h">{{ agent.reportHeadline }}</div>
              <p class="text-medium-emphasis">{{ agent.reportText }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="trace-main">
        <div class="trace-main-h text-disabled">
          <span>Main agent output</span>
          <span>{{ mainTokens }}</span>
        </div>
        <p>{{ mainText }}</p>
      </div>

      <p class="plate-cap text-disabled">
        <v-icon icon="mdi-information-outline" size="15" class="plate-cap__icon" /> {{ disclaimer }}
      </p>
    </v-card>
    <p class="plate-foot text-disabled">{{ foot }}</p>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import ToolCallRow from './ToolCallRow.vue'
import {
  traceTitle,
  traceStamp,
  traceRunMeta,
  traceAgents,
  traceMainTokens,
  traceMainText,
  traceDisclaimer,
  traceFoot,
  TraceAgent
} from './landingContent'

@Options({
  components: { ToolCallRow }
})
export default class TracePlate extends Vue {
  title = traceTitle
  stamp = traceStamp
  runMeta = traceRunMeta
  agents: TraceAgent[] = traceAgents
  mainTokens = traceMainTokens
  mainText = traceMainText
  disclaimer = traceDisclaimer
  foot = traceFoot
  expanded: Record<string, boolean> = {}

  created() {
    // expand the first agent that has a detail trace
    const first = this.agents.find((a) => this.isExpandable(a))
    if (first) {
      this.expanded[first.name] = true
    }
  }

  isExpandable(agent: TraceAgent): boolean {
    return Array.isArray(agent.toolCalls) && agent.toolCalls.length > 0
  }

  toggle(name: string) {
    this.expanded[name] = !this.expanded[name]
  }
}
</script>

<style scoped lang="scss">
.plate {
  margin-top: 24px;
  border: 1px solid var(--l-border);
  overflow: hidden;
}
.plate-header {
  padding: 22px 28px;
  border-bottom: 2px solid rgb(var(--v-theme-msq-yellow));
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}
.plate-title {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.plate-stamp {
  font-family: var(--l-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 4px;
}
.trace-run {
  padding: 18px 24px;
  border-bottom: 1px solid var(--l-border);
}
.trace-run-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.trace-status {
  font-size: 15px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.trace-run-meta {
  font-family: var(--l-mono);
  font-size: 12px;
  letter-spacing: 0.03em;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.pill {
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-accent-green));
  border: 1px solid currentColor;
  border-radius: 9999px;
  padding: 2px 8px;
}
.trace-sub-head {
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 18px 24px 10px;
}
.trace-agents {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ta {
  border: 1px solid var(--l-border);
  border-radius: 8px;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}
.ta-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}
.ta-row--static {
  cursor: default;
}
.ta-name {
  font-family: var(--l-mono);
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.ta-tok {
  font-family: var(--l-mono);
  font-size: 11px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.ta-caret {
  transition: transform 0.2s;
  flex-shrink: 0;
}
.ta-caret--collapsed {
  transform: rotate(-90deg);
}
.ta-detail {
  border-top: 1px solid var(--l-border);
  background: rgb(var(--v-theme-theme-code));
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.ta-report {
  border-top: 1px dashed var(--l-border);
  padding-top: 11px;
}
.ta-report-h {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}
.ta-report p {
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
}
.trace-main {
  padding: 20px 24px;
  border-top: 1px solid var(--l-border);
  margin-top: 8px;
}
.trace-main-h {
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.trace-main p {
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}
.plate-cap {
  font-family: var(--l-mono);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 16px 28px;
  border-top: 1px solid var(--l-border);
  margin: 0;
}
.plate-cap__icon {
  color: rgb(var(--v-theme-accent-ink));
}
.plate-foot {
  font-family: var(--l-mono);
  font-size: 12px;
  line-height: 1.6;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 22px 0 0;
  max-width: 86ch;
}
@media (max-width: 700px) {
  /* token counts drop to their own line under the agent name */
  .ta-row {
    flex-wrap: wrap;
    gap: 6px 14px;
  }
  .ta-name {
    flex: 1 1 auto;
  }
  .ta-tok {
    white-space: normal;
    width: 100%;
    order: 3;
    padding-left: 23px;
  }
}
</style>
